import clsx from "clsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

const sanitizeSchema = {
    ...defaultSchema,
    tagNames: [
        ...(defaultSchema.tagNames || []),
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
    ],
};

type MarkdownProps = {
    testId?: string;
    className?: string;
    children: string;
};

const components: Components = {
    strong: ({ children }) => (
        <strong className="font-bold text-white">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    table: ({ children }) => (
        <div className="my-4 overflow-x-auto">
            <table className="min-w-full border-collapse border border-slate-700">
                {children}
            </table>
        </div>
    ),
    thead: ({ children }) => <thead className="bg-slate-800">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
        <tr className="border-b border-slate-700">{children}</tr>
    ),
    th: ({ children }) => (
        <th
            className={clsx(
                "px-4 py-2",
                "border border-slate-700",
                "text-left font-semibold",
            )}
        >
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="border border-slate-700 px-4 py-2">{children}</td>
    ),
    p: ({ children }) => <p className="body-s">{children}</p>,
    h1: ({ children }) => <h1 className="heading-l my-2">{children}</h1>,
    h2: ({ children }) => <h2 className="heading-m my-2">{children}</h2>,
    h3: ({ children }) => <h3 className="heading-s my-2">{children}</h3>,
    ul: ({ children }) => (
        <ul className={clsx("body-s list-disc space-y-2 pl-6")}>{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className={clsx("body-s list-decimal space-y-2 pl-6")}>
            {children}
        </ol>
    ),
    li: ({ children }) => <li className="body-s subdued">{children}</li>,
    br: () => <br />,
};

export function Markdown({ testId, className, children }: MarkdownProps) {
    return (
        <ReactMarkdown
            className={clsx(className, "body-s", "cursor-default", "subdued")}
            components={components}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
            data-testid={testId}
        >
            {children}
        </ReactMarkdown>
    );
}
